import React, { useEffect, useState, useMemo } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Redirect, Link } from 'react-router-dom';
import useUserStore from '../stores/useUserStore';
import { apiFetchAuthed } from '../apiHelpers';
import { formatEventDate } from '../utils/date-utils';
import { OOOEntry } from './types';
import PageLayout from '../components/ui/PageLayout';
import SegTrack from '../components/ui/SegTrack';
import SearchField from '../components/ui/SearchField';
import Select from '../components/ui/Select';
import Tag from '../components/ui/Tag';
import LogoSpinner from '../components/ui/LogoSpinner';
import { ListRow, ListStack } from '../components/ui/ListRow';
import Tooltip from '../components/ui/Tooltip';
import FadeIn from '../components/ui/FadeIn';
import { CATEGORY } from '../components/ui/categoryColors';

interface CrewMember {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  primary_role: string;
  travel_tier: string | null;
  can_producer: boolean;
  can_pilot: boolean;
  can_operations: boolean;
  can_manufacturing: boolean;
  show_count_monthly: number;
  show_count_trailing: number;
  upcoming_ooo: OOOEntry[];
}

const TIER_OPTIONS: { value: string | null; label: string; color: string }[] = [
  { value: null, label: 'None', color: 'text-on-surface-variant' },
  { value: 'primary', label: 'Primary', color: 'text-green-400' },
  { value: 'backup', label: 'Backup', color: 'text-amber-400' },
];

const RosterPage: React.FC = () => {
  const user = useUserStore(s => s.user);
  const canAccess = user.can_admin || user.can_producer;

  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null); // user id being saved
  const [filter, setFilter] = useState<'all' | 'crew' | 'unassigned'>('all');
  const [search, setSearch] = useState('');

  const fetchCrew = async () => {
    try {
      const data = await apiFetchAuthed('/schedule/crew');
      setCrew(data || []);
    } catch (err) {
      console.error('Error fetching crew:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canAccess) fetchCrew();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess]);

  const handleTierChange = async (userId: string, newTier: string | null) => {
    setSaving(userId);
    try {
      await apiFetchAuthed(`/schedule/crew/${userId}`, {
        method: 'PATCH',
        body: { travel_tier: newTier },
      });
      setCrew(prev =>
        prev.map(u => u.id === userId ? { ...u, travel_tier: newTier } : u)
      );
    } catch (err) {
      console.error('Error updating travel tier:', err);
    } finally {
      setSaving(null);
    }
  };

  const filtered = useMemo(() => {
    let list = crew;
    if (filter === 'crew') list = list.filter(u => u.travel_tier);
    if (filter === 'unassigned') list = list.filter(u => !u.travel_tier);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(u =>
        (u.firstname || '').toLowerCase().includes(q) ||
        (u.lastname || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [crew, filter, search]);

  // Stats
  const stats = useMemo(() => ({
    total: crew.length,
    primary: crew.filter(u => u.travel_tier === 'primary').length,
    backup: crew.filter(u => u.travel_tier === 'backup').length,
    producers: crew.filter(u => u.can_producer && u.travel_tier).length,
  }), [crew]);

  if (!canAccess) return <Redirect to="/" />;

  return (
    <PageLayout width="wide" kind="workspace" className="pt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-on-surface">Roster</h1>
          <p className="text-sm text-on-surface-variant mt-1">
            Set travel tiers to control who appears on the <Link to="/schedule" className="text-primary hover:underline">Schedule</Link> page.
          </p>
        </div>
        <Link
          to="/schedule"
          className={buttonClass('tonal', 'md')}
        >
          Open Schedule
        </Link>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Staff', value: stats.total, color: 'text-on-surface' },
          { label: 'Primary Crew', value: stats.primary, color: 'text-green-400' },
          { label: 'Backup Crew', value: stats.backup, color: 'text-amber-400' },
          { label: 'Producers (travel)', value: stats.producers, color: 'text-purple-400' },
        ].map(s => (
          <div key={s.label} className="bg-gray-850 border border-gray-700/60 rounded-lg px-4 py-3">
            <div className={`text-2xl font-semibold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-on-surface-variant mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <SegTrack
          label="Show staff"
          value={filter}
          onChange={(k) => setFilter(k as any)}
          options={[
            { key: 'all', label: 'All' },
            { key: 'crew', label: 'On Crew' },
            { key: 'unassigned', label: 'Unassigned' },
          ]}
        />
        <SearchField
          label="Search staff by name or email"
          value={search}
          onChange={setSearch}
          placeholder="Search by name or email..."
          className="flex-1"
        />
      </div>

      {loading && <LogoSpinner label="Loading the crew" />}

      {!loading && (
        <FadeIn>
        <ListStack
          items={filtered}
          getKey={member => member.id}
          label="Crew"
          empty={search ? 'No matching staff found.' : 'No staff in this category.'}
          head={(
            <div className="grid grid-cols-[1fr_120px_100px_100px_1fr] gap-2 px-3 pb-2 text-xs text-on-surface-variant font-medium uppercase tracking-wider">
              <div>Name</div>
              <div>Travel Tier</div>
              <div className="text-center">Shows (Mo)</div>
              <div className="text-center">Shows (90d)</div>
              <div>Upcoming OOO</div>
            </div>
          )}
          renderRow={member => {
            const tierOption = TIER_OPTIONS.find(t => t.value === member.travel_tier) || TIER_OPTIONS[0];
            const isSaving = saving === member.id;

            return (
              <ListRow>
              <div
                className={`grid grid-cols-[1fr_120px_100px_100px_1fr] gap-2 items-center transition-colors
                  ${!member.travel_tier ? 'opacity-60 hover:opacity-100' : ''}`}
              >
                {/* Name + role badges */}
                <div>
                  <div className="text-sm text-on-surface font-medium">
                    {member.firstname} {member.lastname}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {member.can_pilot && (
                      <Tag hex={CATEGORY.sky.ink.hex} size="sm">Pilot</Tag>
                    )}
                    {member.can_producer && (
                      <Tag hex={CATEGORY.purple.ink.hex} size="sm">Producer</Tag>
                    )}
                    {member.can_operations && (
                      <Tag hex={CATEGORY.blue.ink.hex} size="sm">Ops</Tag>
                    )}
                    {member.can_manufacturing && (
                      <Tag size="sm">Mfg</Tag>
                    )}
                    <span className="text-[10px] text-on-surface-variant">{member.email}</span>
                  </div>
                </div>

                {/* Travel Tier selector */}
                <div>
                  <Select
                    label={`Travel tier for ${member.firstname} ${member.lastname}`}
                    size="sm"
                    value={member.travel_tier || ''}
                    onChange={(k) => handleTierChange(member.id, k || null)}
                    disabled={isSaving}
                    options={[
                      { key: '', label: 'None' },
                      { key: 'primary', label: 'Primary' },
                      { key: 'backup', label: 'Backup' },
                    ]}
                    renderValue={(o) => <span className={`block truncate font-medium ${tierOption.color}`}>{o?.label ?? 'None'}</span>}
                  />
                </div>

                {/* Monthly show count */}
                <div className="text-center">
                  <span className={`text-sm font-mono ${member.show_count_monthly > 0 ? 'text-on-surface' : 'text-on-surface-disabled'}`}>
                    {member.show_count_monthly}
                  </span>
                </div>

                {/* Trailing 90-day count */}
                <div className="text-center">
                  <span className={`text-sm font-mono ${member.show_count_trailing > 0 ? 'text-on-surface-variant' : 'text-on-surface-disabled'}`}>
                    {member.show_count_trailing}
                  </span>
                </div>

                {/* Upcoming OOO */}
                <div className="flex flex-wrap gap-1">
                  {member.upcoming_ooo.length === 0 && (
                    <span className="text-[10px] text-gray-700">—</span>
                  )}
                  {member.upcoming_ooo.slice(0, 2).map(o => (
                    <Tooltip key={o.id} label={`${o.notes || 'OOO'}${o.entered_in_onpay ? ' (OnPay)' : ''}`}>
                      <Tag size="sm" variant={o.is_tentative ? 'status' : 'code'}>
                        {formatEventDate(o.start_date.slice(0, 10))}
                        {o.start_date.slice(0, 10) !== o.end_date.slice(0, 10)
                          ? `–${formatEventDate(o.end_date.slice(0, 10))}`
                          : ''}
                        {o.is_tentative ? '?' : ''}
                        {o.entered_in_onpay && (
                          <span className="ml-1 text-green-500">$</span>
                        )}
                      </Tag>
                    </Tooltip>
                  ))}
                  {member.upcoming_ooo.length > 2 && (
                    <span className="text-[10px] text-on-surface-variant">+{member.upcoming_ooo.length - 2}</span>
                  )}
                </div>
              </div>
              </ListRow>
            );
          }}
        />
        </FadeIn>
      )}
    </PageLayout>
  );
};

export default RosterPage;
